/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { MultiTagCellProps, MultiTagColumn, TagCellProps, TextColumn } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import { Policy } from '@wsphere/warranties/domain';
import { firstValueFrom, switchMap } from 'rxjs';
import { POLICY_DETAILS_CONFIG } from '../../config/policy-details.config';
import { POLICY_TERM_AUTO_RENEW_CONFIG } from '../../config/policy-term-auto-renew.config';
import { EditPolicyType, PolicyDialog } from '../../dialogs';
import { ValuePathLabelPipe } from '../../pipes/value-path-label.pipe';
import { CoverageService, PolicyService, RatingMatrixService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-details-page',
  templateUrl: './policy-details.page.html',
  standalone: false,
  styles: [
    `
      @media (min-width: 1280px) {
        ws-policy-stats {
          max-width: calc(60% - 16px);
        }
      }
    `,
  ],
})
export class PolicyDetailsPage {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private securitySvc: SecurityService,
    private propertySvc: PropertyService,
    private coverageSvc: CoverageService,
    private ratingMatrixSvc: RatingMatrixService
  ) {
    this.valuePathLabelPipe = new ValuePathLabelPipe(this.ratingMatrixSvc);
  }

  @ViewChild('createCoverageDialog') createCoverageDialog?: PolicyDialog;

  readonly config = POLICY_DETAILS_CONFIG;

  readonly policy$ = this.policySvc.getPolicyObservable();

  currentTab = 0;

  draft = Policy.Status.DRAFT;

  editPolicyType = EditPolicyType.POLICY_DETAILS;

  createCoveragesType = EditPolicyType.CREATE_COVERAGES;

  canEdit$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_Edit', permissionKeys);
    })
  );

  propertySet$ = this.policy$.pipe(
    switchMap(async policy => {
      if (!policy) return null;
      const propertySet = await this.propertySvc.retrievePropertySet(policy.propertySetId);
      propertySet.properties = propertySet.properties?.sort((a, b) => a.order - b.order);
      propertySet.dimensions = propertySet.dimensions?.sort((a, b) => a.order - b.order);

      this.propertySetSnapshot = propertySet;
      this.durationPathLabels.clear();
      this.resolvingDurationPathLabels.clear();

      for (const term of policy.terms ?? []) {
        if (term.durationPropertyPath) this.resolveDurationPathLabel(term.durationPropertyPath);
      }

      this.refreshTermColumns();

      return propertySet;
    })
  );

  private propertySetSnapshot: PropertySet.Model | null = null;
  private readonly durationPathLabels = new Map<string, string>();
  private readonly resolvingDurationPathLabels = new Set<string>();
  private readonly valuePathLabelPipe: ValuePathLabelPipe;

  termColumns = this.buildTermColumns();

  private buildTermColumns() {
    return [
      new TextColumn({ header: 'Order', fitContent: true }, (item: Policy.PolicyTerm) => ({
        text: '' + item.order,
      })),
      new TextColumn({ header: 'Term title' }, (item: Policy.PolicyTerm) => ({
        text: item.title,
      })),
      new TextColumn({ header: 'Term Duration' }, (item: Policy.PolicyTerm) => ({
        text: this.getTermDurationLabel(item),
      })),
      new TextColumn({ header: 'Payment Optional', fitContent: true }, (item: Policy.PolicyTerm) => ({
        text: item.paymentOptional ? 'Yes' : 'No',
      })),
      new TextColumn({ header: 'Auto-Renew', fitContent: true }, (item: Policy.PolicyTerm) => ({
        text: item.autoRenew ? 'Yes' : 'No',
      })),
      new TextColumn(
        { header: POLICY_TERM_AUTO_RENEW_CONFIG.details.header, fitContent: true },
        (item: Policy.PolicyTerm) => ({ text: this.getMaxAutoRenewalsLabel(item) })
      ),
      new MultiTagColumn<Policy.PolicyTerm>({ header: 'Payment Schedules' }, item => ({
        items: this.getScheduleBadges(item, item.paymentSchedules).items,
        limit: 5,
      })),
    ];
  }

  private getMaxAutoRenewalsLabel(term: Policy.PolicyTerm): string {
    const copy = POLICY_TERM_AUTO_RENEW_CONFIG.details;
    if (!term.autoRenew) return copy.notApplicable;
    return term.maxAutoRenewals === null || term.maxAutoRenewals === undefined
      ? copy.unlimited
      : String(term.maxAutoRenewals);
  }

  private refreshTermColumns() {
    this.termColumns = this.buildTermColumns();
  }

  private getScheduleBadges(
    term: Policy.PolicyTerm,
    paymentSchedules: Policy.PaymentSchedule[] | null
  ): MultiTagCellProps {
    if (!paymentSchedules?.length) return { items: [{ text: 'None', theme: 'default' }] };
    else {
      const items: TagCellProps[] = [];

      paymentSchedules.forEach(schedule => {
        if (schedule?.title) {
          items.push({
            text: schedule?.title || '',
            theme: 'default',
            showToolTip: true,

            tooltip:
              schedule?.title !== 'One Time Payment'
                ? {
                    header: schedule.title,
                    text: `${schedule.installments} installment${
                      schedule.installments > 1 ? 's' : ''
                    } / billed every ${Math.floor(term.duration / schedule.installments)} days with a surcharge of ${
                      schedule.rate
                    }%`,
                  }
                : undefined,
          });
        }
      });

      return { items };
    }
  }

  async downloadProvisionTemplate(policy: Policy.Model) {
    if (!policy) return;

    const response = await this.policySvc.generateProvisionTemplate(policy.id).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
    });

    if (response) {
      const link = document.createElement('a');
      link.setAttribute('href', response.data);
      link.setAttribute('download', 'Provisioning.csv');
      link.click();
    }
  }

  getDuration(duration?: number | null) {
    if (!duration) return '0 Days';

    const years = Math.floor(duration / 365);
    const months = Math.floor((duration - 365 * years) / 30);
    const days = Math.floor(duration - 365 * years - 30 * months);

    return (
      (years > 0 ? years + ' Year' + (years > 1 ? 's ' : ' ') : '') +
      (months > 0 ? months + ' Month' + (months > 1 ? 's ' : ' ') : '') +
      (days > 0 ? days + ' Day' + (days > 1 ? 's' : '') : '')
    );
  }

  private getTermDurationLabel(term: Policy.PolicyTerm) {
    const durationPropertyPath = term.durationPropertyPath;
    if (!durationPropertyPath) return this.getDuration(term.duration);

    this.resolveDurationPathLabel(durationPropertyPath);
    return this.durationPathLabels.get(durationPropertyPath) ?? durationPropertyPath;
  }

  private resolveDurationPathLabel(path: string) {
    if (!this.propertySetSnapshot) return;
    if (this.durationPathLabels.has(path) || this.resolvingDurationPathLabels.has(path)) return;

    this.resolvingDurationPathLabels.add(path);
    void firstValueFrom(this.valuePathLabelPipe.transform(path, this.propertySetSnapshot))
      .then(label => {
        this.durationPathLabels.set(path, label || path);
        this.refreshTermColumns();
      })
      .finally(() => {
        this.resolvingDurationPathLabels.delete(path);
      });
  }

  setEditPolicyType(tab: number) {
    this.currentTab = tab;
    switch (tab) {
      case 0:
        this.editPolicyType = EditPolicyType.POLICY_DETAILS;
        break;
      case 1:
        this.editPolicyType = EditPolicyType.POLICY_TERM;
        break;
      case 2:
        this.editPolicyType = EditPolicyType.POLICY_ASSET;
        break;
    }
  }

  async downloadCoverages(policy: Policy.Model) {
    if (!policy) return;

    const response = await this.coverageSvc.export(policy.id).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
    });

    if (response) {
      const link = document.createElement('a');
      link.setAttribute('href', response.data);
      link.setAttribute('download', 'Coverages.csv');
      link.click();
    }
  }

  editPolicy(newPolicyId: string, policy: Policy.Model) {
    if (newPolicyId !== policy?.id) this.navigateTo('../' + newPolicyId);
  }

  createCoverage(event: string) {
    if (event.includes('pol_')) this.navigateTo('../' + event);
  }

  navigateTo(path?: string) {
    if (path) this.router.navigate([`${path}`], { relativeTo: this.route });
  }
}
